import { ServiceType } from '@/enums/services'
import {
  ServiceDetails,
  ServiceGeneral,
  ServiceGeneralPayload,
  ServiceLabel,
  ServiceListItem,
  ServiceListQueryParams,
  ServicePeriodsEndpoint,
  ServicePeriodsPayload,
  ServiceProductGeneralPayload,
  ServiceTimeslot,
  ServiceTimeslotFilterParams,
} from '@/models/service.model'
import { ServicePriceAndDiscount } from '@/models/servicePricing.model'
import {
  OptionKey,
  Paginated,
  PaginationParams,
  StringDuration,
} from '@/models/shared.model'
import { useFilterStore } from '@/store/filter'
import { getOptionKeys, getOrdering } from '@/utils/general'
import axios from 'axios'
import format from 'date-fns/format'
import cloneDeep from 'lodash/cloneDeep'
import { computed, Ref } from 'vue'
import { useMutation, useQuery, useQueryClient } from 'vue-query'

// https://tkdodo.eu/blog/effective-react-query-keys
export const serviceKeys = {
  all: ['services'] as const,
  lists: () => [...serviceKeys.all, 'list'] as const,
  list: (
    companyId: Ref<number | null>,
    filters: Ref<PaginationParams>,
    requestSwitch: Ref<boolean | null>,
    excluded_ids?: Ref<number[]>,
  ) =>
    [
      ...serviceKeys.lists(),
      { companyId, filters, requestSwitch, excluded_ids },
    ] as const,
  listUnpaginated: (
    companyId: Ref<number | null>,
    params: Ref<ServiceListQueryParams> | undefined,
  ) => [...serviceKeys.lists(), 'unpaginated', { companyId, params }] as const,
  details: () => [...serviceKeys.all, 'detail'] as const,
  detail: (id: number) => [...serviceKeys.details(), id] as const,
  // TODO: Review this usage. Does it need to share invalidations, should it be separated?
  timeslots: (
    companyId: Ref<number | null>,
    filters: Ref<ServiceTimeslotFilterParams>,
  ) => [...serviceKeys.lists(), 'timeslots', { companyId, filters }] as const,
  durations: (
    companyId: Ref<number | null>,
    serviceId: Ref<number | null>,
    date: Ref<Date | null>,
  ) =>
    [
      ...serviceKeys.lists(),
      'durations',
      { companyId, serviceId, date },
    ] as const,
  price: (
    serviceId: Ref<number | null>,
    date: Ref<Date | null>,
    timeSlot: Ref<ServiceTimeslot | null>,
    groupSize: Ref<number | null>,
  ) =>
    [
      ...serviceKeys.all,
      'price',
      { serviceId, date, timeSlot, groupSize },
    ] as const,
  categories: (companyId: Ref<number | null>) => [
    ...serviceKeys.all,
    'categories',
    { companyId },
  ],
  labels: () => [...serviceKeys.all, 'label'] as const,
  label: (id: Ref<number | null>) => [...serviceKeys.labels(), id] as const,
  serviceNames: (companyId: Ref<number | null>) =>
    [...serviceKeys.all, 'service-names', { companyId }] as const,
  serviceLabels: (companyId: Ref<number | null>) =>
    [...serviceKeys.all, 'service-labels', { companyId }] as const,
}

interface ServiceFilterParams {
  type?: OptionKey
  state: OptionKey
  private_state: OptionKey
  ordering: string | null
  page: number
  page_size?: number
  labels?: string | null
  search?: string | null
  categories?: string | null
  vatCategories?: string | null
  excluded_ids?: string
  only_published?: boolean
}

export function useServicesQuery(
  companyId: Ref<number | null>,
  pagination: Ref<PaginationParams>,
  requestSwitch: Ref<boolean | null>,
  excluded_ids?: Ref<number[]>,
  only_published?: boolean,
) {
  return useQuery(
    serviceKeys.list(companyId, pagination, requestSwitch, excluded_ids),
    async () => {
      const { serviceFilterObj } = useFilterStore()
      const params: ServiceFilterParams = {
        type: serviceFilterObj.filters.type?.key,
        state: serviceFilterObj.filters.state.key,
        private_state: serviceFilterObj.filters.private_state.key,
        ordering: getOrdering(serviceFilterObj.ordering),
        page: pagination.value.page,
        page_size: pagination.value.page_size,
        labels:
          serviceFilterObj.filters.labels.length !== 0
            ? String(getOptionKeys(serviceFilterObj.filters.labels))
            : null,
        search: serviceFilterObj.filters.search
          ? serviceFilterObj.filters.search
          : null,
        excluded_ids: excluded_ids?.value.join(','),
        only_published: only_published,
      }
      const filteredCategories =
        serviceFilterObj.filters.categories.length !== 0
          ? String(getOptionKeys(serviceFilterObj.filters.categories))
          : null
      if (process.env.VUE_APP_NEW_CATEGORIES_ENABLED) {
        params['vatCategories'] = filteredCategories
      } else {
        params['categories'] = filteredCategories
      }

      const { data } = await axios.get<Paginated<ServiceListItem[]>>(
        `companies/${companyId.value}/services/`,
        {
          params: params,
        },
      )
      return data
    },
    {
      retry: 0,
    },
  )
}

export function useServiceLabelsQuery(companyId: Ref<number | null>) {
  return useQuery(
    serviceKeys.serviceLabels(companyId),
    async () => {
      const { data } = await axios.get<ServiceLabel[]>(
        `companies/${companyId.value}/services/labels/`,
      )
      return data
    },
    {
      enabled: computed(() => !!companyId.value),
      retry: 0,
    },
  )
}

export function useServicesUnpaginatedQuery(
  companyId: Ref<number | null>,
  params: Ref<ServiceListQueryParams> | undefined = undefined,
) {
  return useQuery(
    serviceKeys.listUnpaginated(companyId, params),
    async () => {
      const { data } = await axios.get<ServiceListItem[]>(
        `companies/${companyId.value}/services/unpaginated/`,
        {
          params: params?.value,
        },
      )
      return data
    },
    {
      retry: 0,
    },
  )
}

export function useServicesCategoriesQuery(companyId: Ref<number | null>) {
  return useQuery(
    serviceKeys.categories(companyId),
    async () => {
      const suffix = process.env.VUE_APP_NEW_CATEGORIES_ENABLED
        ? 'vat-categories'
        : 'categories'
      const { data } = await axios.get(
        `companies/${companyId.value}/services/${suffix}/`,
      )
      return data
    },
    {
      retry: 0,
    },
  )
}

export function useServiceQuery(companyId: Ref<number | null>, id: number) {
  return useQuery(serviceKeys.detail(id), () => fetchService(companyId, id), {
    enabled: !!id,
    retry: 0,
  })
}

export function useServicesTimeslotsQuery(
  companyId: Ref<number | null>,
  filters: Ref<ServiceTimeslotFilterParams>,
) {
  return useQuery(
    serviceKeys.timeslots(companyId, filters),
    async () => {
      const { data } = await axios.get<ServiceTimeslot[]>(
        `companies/${companyId.value}/services/timeslots/`,
        {
          params: {
            start:
              filters.value.start && format(filters.value.start, 'yyyy-MM-dd'),
            end: filters.value.end && format(filters.value.end, 'yyyy-MM-dd'),
            ids: filters.value.ids.join(','),
          },
        },
      )
      return data
    },
    {
      enabled: computed(
        () => !!companyId.value && !!filters.value.start && !!filters.value.end,
      ),
      retry: 0,
    },
  )
}

export function useServiceDurationsQuery(
  companyId: Ref<number | null>,
  serviceId: Ref<number | null>,
  date: Ref<Date | null>,
) {
  return useQuery(
    serviceKeys.durations(companyId, serviceId, date),
    async () => {
      const { data } = await axios.get<StringDuration[]>(
        `companies/${companyId.value}/services/${serviceId.value}/durations/`,
        {
          params: {
            date: date.value && format(date.value, 'yyyy-MM-dd'),
          },
        },
      )
      return data
    },
    {
      enabled: computed(
        () => !!companyId.value && !!serviceId.value && !!date.value,
      ),
      retry: 0,
    },
  )
}

export function usePriceQuery(
  serviceId: Ref<number | null>,
  date: Ref<Date | null>,
  timeSlot: Ref<ServiceTimeslot | null>,
  groupSize: Ref<number | null>,
) {
  return useQuery(
    serviceKeys.price(serviceId, date, timeSlot, groupSize),
    async () => {
      const { data } = await axios.get<ServicePriceAndDiscount>(
        `service/${serviceId.value}/price/`,
        {
          params: {
            start: timeSlot.value && timeSlot.value.start,
            end: timeSlot.value && timeSlot.value.end,
            group_size: groupSize.value,
          },
        },
      )
      return data
    },
    {
      enabled: computed(
        () =>
          !!serviceId.value &&
          !!date.value &&
          !!timeSlot.value &&
          !!groupSize.value,
      ),
      retry: 0,
    },
  )
}

export function useUpdateServiceGeneral(
  companyId: Ref<number | null>,
  serviceId: number,
) {
  const queryClient = useQueryClient()

  return useMutation(
    async (form: ServiceGeneralPayload) => {
      const { data } = await axios.put<ServiceGeneral>(
        `companies/${companyId.value}/services/${serviceId}/general/`,
        { ...form },
      )
      return data
    },
    {
      onSuccess: () => {
        // Invalidate all list caches because of pagination.
        // Adding this one to some page might make it go over set page size.
        queryClient.invalidateQueries(serviceKeys.lists())
        // Invalidate service to update computed values associated with tabs being disabled or not.
        queryClient.invalidateQueries(serviceKeys.detail(serviceId))
      },
    },
  )
}

export function useUpdateServiceProductGeneral(
  companyId: Ref<number | null>,
  serviceId: number,
) {
  const queryClient = useQueryClient()

  return useMutation(
    async (form: ServiceProductGeneralPayload) => {
      const { data } = await axios.put<ServiceGeneral>(
        `companies/${companyId.value}/services/product/${serviceId}/general/`,
        { ...form },
      )
      return data
    },
    {
      onSuccess: () => {
        // Invalidate all list caches because of pagination.
        // Adding this one to some page might make it go over set page size.
        queryClient.invalidateQueries(serviceKeys.lists())
        // Invalidate service to update computed values associated with tabs being disabled or not.
        queryClient.invalidateQueries(serviceKeys.detail(serviceId))
      },
    },
  )
}

export function useFetchServicePeriods(
  companyId: Ref<number | null>,
  serviceId: number,
) {
  return useMutation(async () => {
    const { data } = await axios.get<ServicePeriodsEndpoint>(
      `companies/${companyId.value}/services/${serviceId}/periods/`,
    )
    return data
  })
}

export function useUpdateServicePeriods(
  companyId: Ref<number | null>,
  serviceId: number,
) {
  return useMutation(async (form: ServicePeriodsPayload) => {
    // If duration/group size/resources are not important their rows will not be shown.
    // If those are sent they will be validated, but because they are hidden so are their validation errors. Also, user cannot remove last row themselves.
    // Component does not empty those rows right after important toggle because it might happen by accident and we want to show them their previous work again when they retoggle it.
    // But when they click save and this here is called, we don't care about those rows anymore, they are more trouble than needed. This means we will get rid of them.
    // We do it to a cloned object because we don't want to affect the component itself which might've passed their own form object by reference instead of creating fresh payload.
    const payload = cloneDeep(form)
    for (const period of payload.periods) {
      //pricing.price is actually a list of them
      for (const price of period.pricing.price) {
        if (!price.is_duration_important) {
          price.duration_rows = []
        }
        if (!price.is_group_size_important) {
          price.group_size_rows = []
        }
        if (!price.is_resources_important) {
          price.resources_rows = []
        }
      }
    }
    const { data } = await axios.put<ServicePeriodsEndpoint>(
      `companies/${companyId.value}/services/${serviceId}/periods/`,
      payload,
    )
    return data
  })
}

export function useAddServiceDraftMutation(companyId: Ref<number | null>) {
  const queryClient = useQueryClient()

  return useMutation(
    async (payload: {
      type: ServiceType
      name?: string
      compucashProductId?: number
    }) => {
      const { data } = await axios.post<ServiceDetails>(
        `companies/${companyId.value}/services/`,
        payload,
      )
      return data
    },
    {
      onSuccess: (data) => {
        // Invalidate all list caches because of pagination.
        // Adding this one to some page might make it go over set page size.
        queryClient.invalidateQueries(serviceKeys.lists())

        // Set cache of this one service object
        queryClient.setQueryData(serviceKeys.detail(data.id), data)
      },
    },
  )
}

export function useAddDuplicateServiceMutation(companyId: Ref<number | null>) {
  const queryClient = useQueryClient()

  return useMutation(
    async (serviceId: number) => {
      const { data } = await axios.post<ServiceDetails>(
        `companies/${companyId.value}/services/duplicate/`,
        {
          service_id: serviceId,
        },
      )
      return data
    },
    {
      onSuccess: (data) => {
        // Invalidate all list caches because of pagination.
        // Adding this one to some page might make it go over set page size.
        queryClient.invalidateQueries(serviceKeys.lists())

        // Set cache of this one service object
        queryClient.setQueryData(serviceKeys.detail(data.id), data)
      },
    },
  )
}

export function useArchiveServiceMutation(companyId: Ref<number | null>) {
  const queryClient = useQueryClient()

  return useMutation(
    async (serviceId: number) => {
      const { data } = await axios.put<ServiceDetails>(
        `companies/${companyId.value}/services/${serviceId}/archive/`,
      )
      return data
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(serviceKeys.lists())
        queryClient.setQueryData(serviceKeys.detail(data.id), data)
      },
    },
  )
}

async function fetchService(
  companyId: Ref<number | null>,
  id: number,
): Promise<ServiceDetails> {
  const { data } = await axios.get<ServiceDetails>(
    `companies/${companyId.value}/services/${id}/`,
  )
  return data
}

export function useSetServicePrivate(
  companyId: Ref<number | null>,
  serviceId: number,
) {
  const queryClient = useQueryClient()

  return useMutation(
    async (isPrivate: boolean) => {
      const { data } = await axios.put<Record<string, never>>(
        `companies/${companyId.value}/services/${serviceId}/private/`,
        { is_private: isPrivate },
      )
      return data
    },
    {
      onSuccess: () => {
        // Invalidate all list caches because of pagination.
        // Adding this one to some page might make it go over set page size.
        queryClient.invalidateQueries(serviceKeys.lists())
        // Invalidate service to update computed values associated with tabs being disabled or not.
        queryClient.invalidateQueries(serviceKeys.detail(serviceId))
      },
    },
  )
}

export function usePublishService(
  companyId: Ref<number | null>,
  serviceId: number,
) {
  const queryClient = useQueryClient()

  return useMutation(
    async () => {
      const { data } = await axios.put<Record<string, never>>(
        `companies/${companyId.value}/services/${serviceId}/publish/`,
      )
      return data
    },
    {
      onSuccess: () => {
        // Invalidate all list caches because of pagination.
        // Adding this one to some page might make it go over set page size.
        queryClient.invalidateQueries(serviceKeys.lists())
        // Invalidate service to update computed values associated with tabs being disabled or not.
        queryClient.invalidateQueries(serviceKeys.detail(serviceId))
      },
    },
  )
}

export function useServiceNamesQuery(companyId: Ref<number | null>) {
  return useQuery(
    serviceKeys.serviceNames(companyId),
    async () => {
      const { data } = await axios.get<Array<{ id: number; name: string }>>(
        `companies/${companyId.value}/service-names/`,
      )
      return data
    },
    {
      enabled: computed(() => !!companyId.value),
      retry: 0,
    },
  )
}
